import { useQuery } from "@tanstack/react-query";
import { useAuth } from "../hooks/useAuth";
import { adminGet } from "../services/api";
import {
  LineChart as LineChartRaw,
  Line as LineRaw,
  XAxis as XAxisRaw,
  YAxis as YAxisRaw,
  Tooltip as TooltipRaw,
  ResponsiveContainer as ResponsiveContainerRaw,
  CartesianGrid as CartesianGridRaw,
} from "recharts";

// Recharts types clash with React 18 — cast to any to unblock.
const LineChart = LineChartRaw as any;
const Line = LineRaw as any;
const XAxis = XAxisRaw as any;
const YAxis = YAxisRaw as any;
const Tooltip = TooltipRaw as any;
const ResponsiveContainer = ResponsiveContainerRaw as any;
const CartesianGrid = CartesianGridRaw as any;

interface UserStats {
  total_profiles: number;
  active_dau: number;
  active_wau: number;
  active_mau: number;
  retention_d7: number;
  dau_series: { day: string; count: number }[];
  top_categories: { category: string; interactions: number }[];
  feature_usage: {
    chat_queries_24h: number;
    insight_views_24h: number;
    briefing_plays_24h: number;
    article_likes_24h: number;
    bookmarks_24h: number;
  };
}

function Stat({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="card">
      <div className="text-xs text-slate-500 uppercase tracking-wider mb-2">{label}</div>
      <div className="text-2xl font-bold">{value}</div>
      {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
    </div>
  );
}

export default function UsersPage() {
  const { token } = useAuth();
  const { data, isLoading, error } = useQuery<UserStats>({
    queryKey: ["user-stats"],
    queryFn: () => adminGet<UserStats>("/api/v1/admin/stats/users", token),
  });

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-2xl font-semibold">Users & Usage</h1>
        <p className="text-sm text-slate-500 mt-1">Active users, retention, and feature adoption.</p>
      </div>

      {isLoading && <div className="text-slate-500">Loading…</div>}
      {error && <div className="card text-red-400">{(error as Error).message}</div>}

      {data && (
        <>
          <div className="grid grid-cols-4 gap-4 mb-6">
            <Stat label="Total profiles" value={data.total_profiles.toLocaleString()} />
            <Stat label="DAU" value={data.active_dau.toLocaleString()} sub="last 24h" />
            <Stat label="WAU" value={data.active_wau.toLocaleString()} sub="last 7 days" />
            <Stat label="MAU" value={data.active_mau.toLocaleString()} sub="last 30 days" />
          </div>

          <div className="card mb-6">
            <h3 className="text-sm font-semibold text-slate-300 mb-4 uppercase tracking-wider">
              DAU — last 30 days
            </h3>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={data.dau_series}>
                  <CartesianGrid stroke="#1e293b" strokeDasharray="3 3" />
                  <XAxis dataKey="day" stroke="#64748b" fontSize={11}
                    tickFormatter={(d) => new Date(d).toLocaleDateString("en-IN", { month: "short", day: "numeric" })} />
                  <YAxis stroke="#64748b" fontSize={11} />
                  <Tooltip contentStyle={{ backgroundColor: "#020617", border: "1px solid #1e293b" }} />
                  <Line type="monotone" dataKey="count" stroke="#0066FF" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4 mb-6">
            <div className="card">
              <h3 className="text-sm font-semibold text-slate-300 mb-3 uppercase tracking-wider">
                Top categories (24h)
              </h3>
              <div className="space-y-2">
                {data.top_categories.map((c) => (
                  <div key={c.category} className="flex items-center justify-between py-1.5">
                    <span className="text-sm capitalize">{c.category}</span>
                    <span className="font-mono text-xs text-slate-500">{c.interactions}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="card">
              <h3 className="text-sm font-semibold text-slate-300 mb-3 uppercase tracking-wider">
                Feature usage (24h)
              </h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span>Chat queries</span>
                  <span className="font-mono">{data.feature_usage.chat_queries_24h}</span>
                </div>
                <div className="flex justify-between">
                  <span>Insight views</span>
                  <span className="font-mono">{data.feature_usage.insight_views_24h}</span>
                </div>
                <div className="flex justify-between">
                  <span>Briefing plays</span>
                  <span className="font-mono">{data.feature_usage.briefing_plays_24h}</span>
                </div>
                <div className="flex justify-between">
                  <span>Article likes</span>
                  <span className="font-mono">{data.feature_usage.article_likes_24h}</span>
                </div>
                <div className="flex justify-between">
                  <span>Bookmarks</span>
                  <span className="font-mono">{data.feature_usage.bookmarks_24h}</span>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
